import { useCallback, useEffect, useMemo, useState } from 'react';
import { fitLiveApi } from './api';

function pct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Math.round(Number(n) * 100)}%`;
}

const EMPTY_FORM = {
  email: '',
  nombre: '',
  password: '',
  display_name: '',
  bio: '',
  zoom_url: '',
  zoom_passcode: '',
  whatsapp: '',
};

function creatorPhotoSrc(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('data:') || u.startsWith('/')) return u;
  return `/${u}`;
}

/** Usuarios → Entrenadores Fit Live: alta clara + ficha mínima (perfil + Zoom + foto). */
export function FitLiveTrainersRoster() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formAvatarFile, setFormAvatarFile] = useState(null);
  const [formAvatarPreview, setFormAvatarPreview] = useState('');
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({
    display_name: '',
    bio: '',
    zoom_url: '',
    zoom_passcode: '',
    whatsapp: '',
    avatar_url: '',
    password: '',
    payout_bank: '',
    payout_account_number: '',
    payout_account_type: 'ahorros',
    payout_holder_name: '',
    payout_document: '',
  });
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fitLiveApi.masterGet();
      setCreators(Array.isArray(data.creators) ? data.creators : []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los entrenadores Fit Live');
      setCreators([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPickFormAvatar = (file) => {
    setFormAvatarFile(file || null);
    if (formAvatarPreview) URL.revokeObjectURL(formAvatarPreview);
    setFormAvatarPreview(file ? URL.createObjectURL(file) : '');
  };

  const onPickEditAvatar = (file) => {
    setEditAvatarFile(file || null);
    if (editAvatarPreview && editAvatarPreview.startsWith('blob:')) URL.revokeObjectURL(editAvatarPreview);
    setEditAvatarPreview(file ? URL.createObjectURL(file) : '');
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterCreatorCreate({
        email: form.email,
        nombre: form.nombre || form.display_name,
        password: form.password,
        display_name: form.display_name || form.nombre,
        bio: form.bio,
        zoom_url: form.zoom_url,
        zoom_passcode: form.zoom_passcode,
        whatsapp: form.whatsapp,
      });
      let creatorsList = Array.isArray(res.creators) ? res.creators : [];
      const newId = res.creator?.id;
      if (newId && formAvatarFile) {
        const up = await fitLiveApi.masterCreatorAvatar(newId, formAvatarFile);
        creatorsList = Array.isArray(up.creators) ? up.creators : creatorsList;
      }
      setCreators(creatorsList);
      setMsg(res.created_user
        ? `Creator creado. Login: ${res.user?.email}`
        : `Creator activado sobre cuenta existente: ${res.user?.email}`);
      setForm({ ...EMPTY_FORM });
      onPickFormAvatar(null);
      setShowForm(false);
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = {
        invalid_email: 'Correo inválido',
        nombre_required: 'Nombre obligatorio para cuenta nueva',
        password_min_8: 'Contraseña mínima 8 caracteres (cuenta nueva)',
        user_inactive: 'Cuenta desactivada; reactívala primero',
      };
      setError(map[code] || code || 'No se pudo crear el creator');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (c) => {
    setEditId(c.id);
    setEdit({
      display_name: c.display_name || '',
      bio: c.bio || '',
      zoom_url: c.zoom_url || '',
      zoom_passcode: c.zoom_passcode || '',
      whatsapp: c.whatsapp || '',
      avatar_url: c.avatar_url || '',
      password: '',
      payout_bank: c.payout_bank || '',
      payout_account_number: c.payout_account_number || '',
      payout_account_type: c.payout_account_type || 'ahorros',
      payout_holder_name: c.payout_holder_name || '',
      payout_document: c.payout_document || '',
    });
    setEditAvatarFile(null);
    setEditAvatarPreview(creatorPhotoSrc(c.avatar_url));
  };

  const saveEdit = async () => {
    if (!editId) return;
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const { avatar_url: _a, password, ...rest } = edit;
      const patch = { ...rest };
      if (password && String(password).trim()) {
        patch.password = String(password).trim();
        patch.activate_user = true;
      }
      let creator = null;
      const res = await fitLiveApi.masterCreatorPatch(editId, patch);
      creator = res.creator;
      if (editAvatarFile) {
        const up = await fitLiveApi.masterCreatorAvatar(editId, editAvatarFile);
        creator = up.creator || creator;
        if (Array.isArray(up.creators)) setCreators(up.creators);
      }
      setCreators((rows) => rows.map((r) => (r.id === editId ? { ...r, ...creator, avatar_url: creator?.avatar_url || r.avatar_url } : r)));
      const loginEmail = creator?.email || res.login_user?.email;
      setMsg(password
        ? `Ficha guardada. Login: ${loginEmail || 'email de la ficha'} · clave actualizada (cuenta activada)`
        : 'Ficha creator guardada');
      setEditId(null);
      setEditAvatarFile(null);
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = { password_min_8: 'Clave de acceso: mínimo 8 caracteres' };
      setError(map[code] || code || 'Error al guardar ficha');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteCreator = async (c) => {
    const label = c.display_name || c.id;
    if (!window.confirm(`¿Quitar a «${label}» del catálogo Fit Live?\nLa cuenta shell se conserva; las clases publicadas pasan a borrador.`)) {
      return;
    }
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterCreatorDelete(c.id);
      if (Array.isArray(res.creators)) setCreators(res.creators);
      else setCreators((rows) => rows.filter((r) => r.id !== c.id));
      if (editId === c.id) setEditId(null);
      setMsg(`Creator «${label}» eliminado del catálogo Fit Live`);
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = {
        not_found: 'No se encontró ese creator en el catálogo (¿ya lo borraste?)',
        forbidden: 'Sin permiso para borrar creators',
      };
      setError(map[code] || code || 'No se pudo borrar el creator');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 surface-readable">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-stone-600 max-w-xl">
          Creators Fit Live (clases en vivo). Independiente de Training y de marca blanca.
          Alta aquí (o en Fit Live → Usuarios) → el creator inicia sesión y usa Creator Studio.
        </p>
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => { setShowForm((v) => !v); setMsg(''); setError(''); }}
        >
          {showForm ? 'Cerrar formulario' : 'Crear Creator'}
        </button>
      </div>

      {showForm ? (
        <form
          onSubmit={onCreate}
          className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 ui-light-panel"
        >
          <h3 className="font-semibold text-stone-900 text-sm">Nuevo Creator Fit Live</h3>
          <p className="text-xs text-stone-500">
            Si el correo ya existe, solo se activa el acceso creator (no cambia Training/Food).
            Si es nuevo, crea cuenta usuario final + perfil.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-stone-700">
              Correo *
              <input
                className="input mt-1 w-full"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700">
              Nombre (cuenta / público)
              <input
                className="input mt-1 w-full"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700">
              Contraseña (solo cuenta nueva, mín. 8)
              <input
                className="input mt-1 w-full"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700">
              Nombre público (landing)
              <input
                className="input mt-1 w-full"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700 sm:col-span-2">
              Bio breve
              <textarea
                className="input mt-1 w-full"
                rows={2}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700 sm:col-span-2">
              Zoom / Meet (enlace)
              <input
                className="input mt-1 w-full"
                placeholder="https://zoom.us/j/…"
                value={form.zoom_url}
                onChange={(e) => setForm({ ...form, zoom_url: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700">
              Passcode Zoom (opcional)
              <input
                className="input mt-1 w-full"
                value={form.zoom_passcode}
                onChange={(e) => setForm({ ...form, zoom_passcode: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700">
              WhatsApp creator
              <input
                className="input mt-1 w-full"
                placeholder="57300…"
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              />
            </label>
            <label className="text-sm text-stone-700 sm:col-span-2">
              Foto / logo (landing Fit Live)
              <input
                className="input mt-1 w-full"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(e) => onPickFormAvatar(e.target.files?.[0] || null)}
              />
              <span className="block text-xs text-stone-500 mt-1">PNG/JPG/WEBP · máx. 2 MB. Se muestra en el login principal.</span>
            </label>
            {formAvatarPreview ? (
              <div className="sm:col-span-2">
                <img
                  src={formAvatarPreview}
                  alt="Vista previa"
                  className="w-20 h-20 rounded-full object-cover border border-slate-200"
                />
              </div>
            ) : null}
          </div>
          <button type="submit" className="btn-primary text-sm" disabled={busy}>
            {busy ? 'Guardando…' : 'Crear / activar Creator'}
          </button>
        </form>
      ) : null}

      {msg ? <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">{msg}</p> : null}
      {error ? <p className="text-sm text-red-700">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-stone-500">Cargando creators…</p>
      ) : creators.length === 0 ? (
        <p className="p-6 text-sm text-stone-500 text-center bg-white rounded-2xl border border-slate-200">
          Aún no hay creators. Usa <strong>Crear Creator</strong> arriba.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {creators.map((c) => (
            <article
              key={c.id}
              className="bg-white rounded-2xl border border-slate-200 p-4 ui-light-panel space-y-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {creatorPhotoSrc(c.avatar_url) ? (
                    <img
                      src={creatorPhotoSrc(c.avatar_url)}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-stone-100 border border-slate-200 shrink-0 flex items-center justify-center text-xs text-stone-400">
                      Sin foto
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-stone-900 truncate">{c.display_name || 'Sin nombre'}</h3>
                    <p className="text-xs text-stone-500">
                      Ficha creator · estado {c.status || 'active'}
                      {c.user_id != null ? ` · user #${c.user_id}` : ''}
                      {c.user_activo === false ? ' · cuenta inactiva' : ''}
                    </p>
                    <p className="text-xs text-stone-700 mt-0.5 break-all">
                      <span className="font-semibold">Login:</span>
                      {' '}
                      {c.email || '— (sin email en cuenta)'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button type="button" className="btn-secondary text-xs" onClick={() => openEdit(c)} disabled={busy}>
                    Editar ficha
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-xs text-red-700 border-red-200"
                    onClick={() => onDeleteCreator(c)}
                    disabled={busy}
                  >
                    Borrar
                  </button>
                </div>
              </div>
              {c.bio ? <p className="text-sm text-stone-600 line-clamp-2">{c.bio}</p> : (
                <p className="text-sm text-stone-400">Sin bio</p>
              )}
              <dl className="text-xs text-stone-600 space-y-1">
                <div><dt className="inline font-semibold">Zoom: </dt><dd className="inline break-all">{c.zoom_url || '—'}</dd></div>
                <div><dt className="inline font-semibold">WhatsApp: </dt><dd className="inline">{c.whatsapp || '—'}</dd></div>
                <div>
                  <dt className="inline font-semibold">Pago: </dt>
                  <dd className="inline">
                    {c.payout_bank
                      ? `${c.payout_bank} · ${c.payout_account_type || '—'} · ${c.payout_account_number || '—'} · ${c.payout_holder_name || '—'} · CC ${c.payout_document || '—'}`
                      : 'Sin cuenta bancaria'}
                  </dd>
                </div>
              </dl>
              {editId === c.id ? (
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <p className="text-xs font-semibold text-stone-800">Perfil público + Zoom + foto</p>
                  <div className="flex items-center gap-3">
                    {editAvatarPreview ? (
                      <img
                        src={editAvatarPreview}
                        alt=""
                        className="w-16 h-16 rounded-full object-cover border border-slate-200"
                      />
                    ) : null}
                    <label className="text-xs text-stone-700 flex-1">
                      Foto / logo
                      <input
                        className="input mt-1 w-full text-sm"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={(e) => onPickEditAvatar(e.target.files?.[0] || null)}
                      />
                    </label>
                  </div>
                  <input
                    className="input w-full text-sm"
                    placeholder="Nombre público"
                    value={edit.display_name}
                    onChange={(e) => setEdit({ ...edit, display_name: e.target.value })}
                  />
                  <textarea
                    className="input w-full text-sm"
                    rows={2}
                    placeholder="Bio"
                    value={edit.bio}
                    onChange={(e) => setEdit({ ...edit, bio: e.target.value })}
                  />
                  <input
                    className="input w-full text-sm"
                    placeholder="https://zoom.us/j/…"
                    value={edit.zoom_url}
                    onChange={(e) => setEdit({ ...edit, zoom_url: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="input w-full text-sm"
                      placeholder="Passcode Zoom (reunión)"
                      value={edit.zoom_passcode}
                      onChange={(e) => setEdit({ ...edit, zoom_passcode: e.target.value })}
                    />
                    <input
                      className="input w-full text-sm"
                      placeholder="WhatsApp (solo admin)"
                      value={edit.whatsapp}
                      onChange={(e) => setEdit({ ...edit, whatsapp: e.target.value })}
                    />
                  </div>
                  <p className="text-xs font-semibold text-stone-800 m-0">Cuenta de pago (liquidación)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="input w-full text-sm"
                      placeholder="Banco"
                      value={edit.payout_bank}
                      onChange={(e) => setEdit({ ...edit, payout_bank: e.target.value })}
                    />
                    <input
                      className="input w-full text-sm"
                      placeholder="Número de cuenta"
                      value={edit.payout_account_number}
                      onChange={(e) => setEdit({ ...edit, payout_account_number: e.target.value })}
                    />
                    <select
                      className="input w-full text-sm"
                      value={edit.payout_account_type}
                      onChange={(e) => setEdit({ ...edit, payout_account_type: e.target.value })}
                    >
                      <option value="ahorros">Ahorros</option>
                      <option value="corriente">Corriente</option>
                    </select>
                    <input
                      className="input w-full text-sm"
                      placeholder="Nombre titular"
                      value={edit.payout_holder_name}
                      onChange={(e) => setEdit({ ...edit, payout_holder_name: e.target.value })}
                    />
                    <input
                      className="input w-full text-sm col-span-2"
                      placeholder="Cédula"
                      value={edit.payout_document}
                      onChange={(e) => setEdit({ ...edit, payout_document: e.target.value })}
                    />
                  </div>
                  <label className="block text-xs text-stone-700">
                    Clave de acceso (login al ecosistema) — mín. 8 · deja vacío para no cambiar
                    <input
                      className="input mt-1 w-full text-sm"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Nueva contraseña de login"
                      value={edit.password}
                      onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                    />
                  </label>
                  <p className="text-xs text-stone-500 m-0">
                    El «Passcode Zoom» es solo la reunión. Para entrar como creator usa el email de Login + esta clave.
                  </p>
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary text-sm" disabled={busy} onClick={saveEdit}>
                      Guardar ficha
                    </button>
                    <button type="button" className="btn-secondary text-sm" onClick={() => setEditId(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

export function FitLiveTrackingPanel({ onBack }) {
  const [creators, setCreators] = useState([]);
  const [creatorId, setCreatorId] = useState('');
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [master, snap] = await Promise.all([
        fitLiveApi.masterGet(),
        fitLiveApi.snapshot().catch(() => null),
      ]);
      setCreators(Array.isArray(master.creators) ? master.creators : []);
      setDash(snap?.control_tower || null);
    } catch (e) {
      setError(e.message || 'Error al cargar seguimiento Fit Live');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byCreator = useMemo(() => dash?.by_creator || [], [dash]);
  const filtered = useMemo(() => {
    if (!creatorId) return byCreator;
    return byCreator.filter((c) => String(c.creator_id || c.id) === String(creatorId)
      || String(c.name) === String(creators.find((x) => x.id === creatorId)?.display_name));
  }, [byCreator, creatorId, creators]);

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const csv = await fitLiveApi.attendanceCsv();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'fit-live-asistencia.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Error CSV');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 d28d-admin-surface surface-readable ui-light-panel p-4 rounded-2xl">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div>
          <button type="button" className="btn-secondary text-sm mb-2" onClick={onBack}>
            Volver
          </button>
          <h1 className="text-xl font-bold text-stone-900">Seguimiento Fit Live</h1>
          <p className="text-sm text-stone-600">Asistencia y KPIs por creator (solo super_admin).</p>
        </div>
        <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={downloadCsv}>
          CSV asistencia
        </button>
      </div>
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      {loading ? (
        <p className="text-sm text-stone-500">Cargando…</p>
      ) : (
        <>
          <label className="block text-sm text-stone-700 max-w-xs">
            Filtrar creator
            <select
              className="input mt-1 w-full"
              value={creatorId}
              onChange={(e) => setCreatorId(e.target.value)}
            >
              <option value="">Todos</option>
              {creators.map((c) => (
                <option key={c.id} value={c.id}>{c.display_name}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <p className="text-xs text-stone-500">Creators</p>
              <p className="text-2xl font-bold text-stone-900">{creators.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <p className="text-xs text-stone-500">Filas KPI</p>
              <p className="text-2xl font-bold text-stone-900">{filtered.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Creator</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Tokens</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Asistencia</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Ocupación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-stone-500">
                      Sin datos de KPI aún. Publica clases y genera reservas.
                    </td>
                  </tr>
                ) : filtered.map((c) => (
                  <tr key={c.creator_id || c.name}>
                    <td className="px-3 py-2 text-stone-800">{c.name}</td>
                    <td className="px-3 py-2">{c.tokens_consumed ?? c.tokens ?? 0}</td>
                    <td className="px-3 py-2">{pct(c.attendance_rate ?? c.attendance)}</td>
                    <td className="px-3 py-2">{pct(c.occupancy_rate ?? c.occupancy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-primary text-sm" onClick={load} disabled={loading}>
            Actualizar
          </button>
        </>
      )}
    </div>
  );
}

const EMPTY_ATHLETE = {
  email: '',
  nombre: '',
  password: '',
  initial_tokens: '0',
};

/**
 * Fit Live → Usuarios (super_admin): creators, atletas finales y vigencias (saldos / bono).
 * Reorganiza el alta que ya existía en Usuarios → Entrenadores Fit Live.
 */
export function FitLiveUsersAdmin() {
  const [panel, setPanel] = useState('creators');
  const [athletes, setAthletes] = useState([]);
  const [vigencias, setVigencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_ATHLETE });
  const [editAthleteId, setEditAthleteId] = useState(null);
  const [editAthlete, setEditAthlete] = useState({ nombre: '', email: '' });
  const [rowTokens, setRowTokens] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fitLiveApi.masterUsers();
      setAthletes(Array.isArray(data.athletes) ? data.athletes : []);
      setVigencias(Array.isArray(data.vigencias) ? data.vigencias : []);
    } catch (e) {
      setError(e.message || 'No se pudo cargar el directorio Fit Live');
      setAthletes([]);
      setVigencias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyAthleteResult = (res) => {
    if (Array.isArray(res.athletes)) setAthletes(res.athletes);
    if (Array.isArray(res.vigencias)) setVigencias(res.vigencias);
  };

  const openEditAthlete = (a) => {
    setEditAthleteId(a.user_id);
    setEditAthlete({ nombre: a.nombre || '', email: a.email || '' });
  };

  const saveEditAthlete = async () => {
    if (!editAthleteId) return;
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterAthletePatch(editAthleteId, {
        nombre: editAthlete.nombre,
        email: editAthlete.email,
      });
      applyAthleteResult(res);
      setMsg('Usuario final actualizado');
      setEditAthleteId(null);
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = {
        invalid_email: 'Correo inválido',
        email_taken: 'Ese correo ya está en uso por otra cuenta',
      };
      setError(map[code] || code || 'No se pudo editar el usuario');
    } finally {
      setBusy(false);
    }
  };

  const adminErr = (err, fallback) => {
    const code = err?.payload?.error || err?.message || '';
    const map = {
      forbidden: 'Sin permiso de super_admin',
      not_found: 'Usuario o compra no encontrada',
      user_is_creator: 'Esa cuenta es creator; no se edita aquí',
      fit_live_storage_not_ready: 'Almacén Fit Live aún arrancando; reintenta en unos segundos',
      fit_live_storage_unavailable: 'Almacén Fit Live no disponible; revisa shell-backend / BD',
      purchase_not_found: 'Compra no encontrada para acreditar',
      purchase_missing_reference: 'La compra no tiene referencia Wompi',
    };
    return map[code] || code || fallback;
  };

  const onActivateAthlete = async (a) => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterAthletePatch(a.user_id, { activo: true, fit_live: true });
      applyAthleteResult(res);
      if (!res.athletes) await load();
      setMsg(`Usuario ${a.email || a.user_id} activado (acceso Fit Live). Si hay compra pendiente, usa «Acreditar pago».`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(adminErr(err, 'No se pudo activar'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const onDeleteAthlete = async (a) => {
    if (!window.confirm(`¿Desactivar a ${a.email || a.nombre || a.user_id}?\nPodrás reactivarlo después. No se borra el historial de tokens.`)) {
      return;
    }
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterAthleteDelete(a.user_id);
      applyAthleteResult(res);
      setMsg(`Usuario ${a.email || a.user_id} desactivado`);
      if (editAthleteId === a.user_id) setEditAthleteId(null);
    } catch (err) {
      setError(err.message || 'No se pudo borrar / desactivar');
    } finally {
      setBusy(false);
    }
  };

  const onCreateAthlete = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.masterAthleteCreate({
        email: form.email,
        nombre: form.nombre,
        password: form.password,
        initial_tokens: Number(form.initial_tokens) || 0,
      });
      setAthletes(Array.isArray(res.athletes) ? res.athletes : []);
      setVigencias(Array.isArray(res.vigencias) ? res.vigencias : []);
      setMsg(res.created_user
        ? `Atleta creado. Login: ${res.user?.email}`
        : `Atleta activado: ${res.user?.email}`);
      setForm({ ...EMPTY_ATHLETE });
      setShowForm(false);
    } catch (err) {
      const code = err.payload?.error || err.message;
      const map = {
        invalid_email: 'Correo inválido',
        nombre_required: 'Nombre obligatorio para cuenta nueva',
        password_min_8: 'Contraseña mínima 8 caracteres (cuenta nueva)',
        user_inactive: 'Cuenta desactivada; reactívala primero',
        user_is_creator: 'Ese correo ya es creator; no lo conviertas en atleta aquí',
      };
      setError(map[code] || code || 'No se pudo crear el atleta');
    } finally {
      setBusy(false);
    }
  };

  const onGrantRowCourtesy = async (a) => {
    const tokens = Number(rowTokens[a.user_id] ?? 10);
    if (!Number.isFinite(tokens) || tokens < 1) {
      setError('Indica tokens de cortesía (≥ 1)');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.grantBonus({
        user_id: a.user_id,
        tokens,
        reason: 'cortesia',
      });
      applyAthleteResult(res);
      if (!res.athletes) await load();
      const totalBonus = res.athletes?.find((x) => Number(x.user_id) === Number(a.user_id))?.bonus_tokens;
      setMsg(
        `Cortesía: +${tokens} tokens a ${a.email || a.user_id}`
        + (totalBonus != null ? ` · Total gratis acumulados: ${totalBonus}` : ''),
      );
      setRowTokens((m) => ({ ...m, [a.user_id]: '10' }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(adminErr(err, 'No se pudo otorgar tokens'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const onConfirmPurchase = async (a) => {
    const pid = a.purchase_pending?.id || a.purchase_pending?.reference;
    if (!pid) {
      setError('No hay compra pendiente para acreditar en esta fila');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!window.confirm(
      `¿Confirmar pago Wompi y acreditar ${a.purchase_pending.tokens} tokens a ${a.email || a.user_id}?\nPack: ${a.purchase_pending.package_name || a.purchase_pending.package_id}`,
    )) return;
    setBusy(true);
    setMsg('Acreditando pago…');
    setError('');
    try {
      const res = await fitLiveApi.masterPurchaseConfirm(pid);
      applyAthleteResult(res);
      if (!res.athletes) await load();
      setMsg(res.skipped
        ? 'Compra ya estaba pagada'
        : `Pago confirmado · +${res.tokens || a.purchase_pending.tokens} tokens · acceso activado`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(adminErr(err, 'No se pudo confirmar la compra'));
      setMsg('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  const onExpireWelcome = async () => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fitLiveApi.expireWelcomeDue();
      setMsg(`Bonos vencidos: ${res.processed || 0} cuentas · ${res.tokens_expired || 0} tokens`);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo barrer bonos vencidos');
    } finally {
      setBusy(false);
    }
  };

  const purchaseCell = (a) => {
    const pending = a.purchase_pending;
    const paid = a.purchase_last_paid;
    if (pending) {
      return (
        <div className="text-xs space-y-0.5">
          <div className="font-semibold text-amber-800">Pendiente Wompi</div>
          <div className="text-stone-800">
            {pending.package_name || pending.package_id}
            {' · '}
            <strong>{pending.tokens}</strong>
            {' tok'}
          </div>
          <div className="text-stone-500">
            $
            {Number(pending.price_cop || 0).toLocaleString('es-CO')}
            {' · '}
            {String(pending.created_at || '').slice(0, 16).replace('T', ' ')}
          </div>
        </div>
      );
    }
    if (paid) {
      return (
        <div className="text-xs space-y-0.5">
          <div className="font-semibold text-emerald-800">Pagado</div>
          <div className="text-stone-800">
            {paid.package_name || paid.package_id}
            {' · '}
            <strong>{paid.tokens}</strong>
            {' tok'}
          </div>
          <div className="text-stone-500">
            $
            {Number(paid.price_cop || 0).toLocaleString('es-CO')}
            {paid.paid_at ? ` · ${String(paid.paid_at).slice(0, 10)}` : ''}
          </div>
        </div>
      );
    }
    return <span className="text-stone-400 text-xs">Sin compra</span>;
  };

  return (
    <div className="space-y-4 surface-readable d28d-admin-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-stone-900 m-0">Usuarios Fit Live</h2>
          <p className="text-sm text-stone-600 max-w-2xl mt-1 mb-0">
            Alta y control de creators y atletas (usuario final), más saldos y bono de bienvenida.
            Independiente de Food / Training / academia.
          </p>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={load} disabled={loading || busy}>
          Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Paneles usuarios Fit Live">
        {[
          { id: 'creators', label: 'Creators' },
          { id: 'athletes', label: 'Usuarios finales' },
          { id: 'vigencias', label: 'Vigencias / tokens' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={panel === t.id}
            className={panel === t.id ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
            onClick={() => setPanel(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p> : null}
      {msg ? <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{msg}</p> : null}

      {panel === 'creators' ? (
        <FitLiveTrainersRoster />
      ) : null}

      {panel === 'athletes' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-stone-600 max-w-xl m-0">
              Atletas del marketplace. Aquí ves compra Wompi (pack/tokens), activas, editas y das cortesía en la misma fila.
            </p>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? 'Cerrar formulario' : 'Crear usuario final'}
            </button>
          </div>

          {showForm ? (
            <form
              onSubmit={onCreateAthlete}
              className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3 max-w-xl ui-light-panel"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="font-medium text-stone-800">Nombre</span>
                  <input
                    className="input w-full mt-1"
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-stone-800">Email</span>
                  <input
                    type="email"
                    className="input w-full mt-1"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-stone-800">Contraseña (nueva)</span>
                  <input
                    type="password"
                    className="input w-full mt-1"
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Mín. 8 si es cuenta nueva"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-stone-800">Tokens iniciales</span>
                  <input
                    type="number"
                    min={0}
                    className="input w-full mt-1"
                    value={form.initial_tokens}
                    onChange={(e) => setForm({ ...form, initial_tokens: e.target.value })}
                  />
                </label>
              </div>
              <button type="submit" className="btn-primary text-sm" disabled={busy}>
                Guardar atleta
              </button>
            </form>
          ) : null}

          {loading ? (
            <p className="text-sm text-stone-500">Cargando atletas…</p>
          ) : athletes.length === 0 ? (
            <p className="text-sm text-stone-500 bg-white rounded-2xl border border-slate-200 px-4 py-6 text-center">
              Aún no hay atletas marcados Fit Live. Crea uno aquí o espera a que compren tokens.
            </p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">ID</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Nombre</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Saldo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Compra Wompi</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Bono</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {athletes.map((a) => (
                    <tr
                      key={a.user_id}
                      className={
                        a.purchase_pending
                          ? 'bg-amber-50/80'
                          : (a.status === 'inactive' || a.status === 'sin_acceso' ? 'bg-stone-50' : '')
                      }
                    >
                      <td className="px-3 py-2 text-stone-600">{a.user_id}</td>
                      <td className="px-3 py-2 text-stone-800">
                        {editAthleteId === a.user_id ? (
                          <input
                            className="input w-full text-sm"
                            value={editAthlete.nombre}
                            onChange={(e) => setEditAthlete({ ...editAthlete, nombre: e.target.value })}
                          />
                        ) : (a.nombre || '—')}
                      </td>
                      <td className="px-3 py-2">
                        {editAthleteId === a.user_id ? (
                          <input
                            className="input w-full text-sm"
                            type="email"
                            value={editAthlete.email}
                            onChange={(e) => setEditAthlete({ ...editAthlete, email: e.target.value })}
                          />
                        ) : (a.email || '—')}
                      </td>
                      <td className="px-3 py-2 text-stone-600">
                        <div>
                          {a.status === 'inactive' ? 'Inactivo' : a.status === 'sin_acceso' ? 'Sin acceso' : 'Activo'}
                        </div>
                        {a.needs_activation ? (
                          <div className="text-xs font-semibold text-amber-800">Requiere activación</div>
                        ) : null}
                        {a.purchase_pending ? (
                          <div className="text-xs font-semibold text-amber-800">Pago pendiente · acreditar</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 font-medium">{a.balance}</td>
                      <td className="px-3 py-2">{purchaseCell(a)}</td>
                      <td className="px-3 py-2 text-stone-700 text-xs">
                        <div className="font-semibold text-stone-900">
                          {Number(a.bonus_tokens) > 0 ? `+${a.bonus_tokens} gratis` : '0 gratis'}
                        </div>
                        {a.welcome?.active
                          ? (
                            <div className="text-stone-500 mt-0.5">
                              Bienvenida hasta
                              {' '}
                              {String(a.welcome.expires_at || '').slice(0, 10)}
                            </div>
                          )
                          : a.welcome?.expired_at
                            ? <div className="text-stone-500 mt-0.5">Bienvenida vencida</div>
                            : null}
                      </td>
                      <td className="px-3 py-2">
                        {editAthleteId === a.user_id ? (
                          <div className="flex flex-wrap gap-1">
                            <button type="button" className="btn-primary text-xs" disabled={busy} onClick={saveEditAthlete}>
                              Guardar
                            </button>
                            <button type="button" className="btn-secondary text-xs" onClick={() => setEditAthleteId(null)}>
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1.5 min-w-[11rem]">
                            <div className="flex flex-wrap gap-1">
                              <button type="button" className="btn-secondary text-xs" disabled={busy} onClick={() => openEditAthlete(a)}>
                                Editar
                              </button>
                              {(a.status === 'inactive' || a.status === 'sin_acceso' || a.needs_activation) ? (
                                <button type="button" className="btn-primary text-xs" disabled={busy} onClick={() => onActivateAthlete(a)}>
                                  Activar
                                </button>
                              ) : null}
                              {a.purchase_pending ? (
                                <button type="button" className="btn-primary text-xs" disabled={busy} onClick={() => onConfirmPurchase(a)}>
                                  Acreditar pago
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="btn-secondary text-xs text-red-700 border-red-200"
                                disabled={busy || a.status === 'inactive'}
                                onClick={() => onDeleteAthlete(a)}
                              >
                                Borrar
                              </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-1">
                              <input
                                className="input w-16 text-xs py-1"
                                type="number"
                                min={1}
                                title="Tokens cortesía"
                                value={rowTokens[a.user_id] ?? '10'}
                                onChange={(e) => setRowTokens((m) => ({ ...m, [a.user_id]: e.target.value }))}
                              />
                              <button
                                type="button"
                                className="btn-secondary text-xs"
                                disabled={busy}
                                onClick={() => onGrantRowCourtesy(a)}
                              >
                                Cortesía
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {panel === 'vigencias' ? (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-sm text-stone-700 space-y-2 ui-light-panel max-w-3xl">
            <p className="font-semibold text-stone-900 m-0">Vigencias = misma tabla de usuarios</p>
            <p className="m-0">
              Activar, cortesía y acreditar Wompi se hacen en
              {' '}
              <strong>Usuarios finales</strong>
              {' '}
              (columna Compra Wompi + acciones por fila). Aquí solo ves el resumen y puedes barrer bonos vencidos.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={onExpireWelcome}>
              Barrer bonos vencidos
            </button>
            <button type="button" className="btn-primary text-sm" onClick={() => setPanel('athletes')}>
              Ir a Usuarios finales
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-stone-500">Cargando vigencias…</p>
          ) : vigencias.length === 0 ? (
            <p className="text-sm text-stone-500 bg-white rounded-2xl border border-slate-200 px-4 py-6 text-center">
              Nadie con saldo, bono o compra todavía.
            </p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Tipo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">User</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Nombre / email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Saldo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Compra Wompi</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-stone-600">Bono</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vigencias.map((r) => (
                    <tr key={`${r.kind}-${r.user_id}`} className={r.purchase_pending ? 'bg-amber-50/80' : ''}>
                      <td className="px-3 py-2 capitalize text-stone-600">{r.kind === 'creator' ? 'Creator' : 'Final'}</td>
                      <td className="px-3 py-2">{r.user_id}</td>
                      <td className="px-3 py-2">
                        <div className="text-stone-800">{r.display_name || r.nombre || '—'}</div>
                        <div className="text-xs text-stone-500">{r.email || ''}</div>
                      </td>
                      <td className="px-3 py-2 font-medium">{r.balance}</td>
                      <td className="px-3 py-2">{purchaseCell(r)}</td>
                      <td className="px-3 py-2 text-stone-600">
                        {r.welcome?.active
                          ? `${r.welcome.tokens} tk · vence ${String(r.welcome.expires_at || '').slice(0, 10)}`
                          : r.welcome?.expired_at
                            ? `Venció (${r.welcome.expired_tokens || 0} tk)`
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
